import React, { Component } from 'react';
import styled from 'styled-components';
import _ from 'lodash';
import { sprintf } from 'sprintf-js';
import { normalizeString } from '../../../utils/index';

import FormHint from 'common/components/styleguide/form/_helpers/FormHint';
import FormError from 'common/components/styleguide/form/_helpers/FormError';
import FormLabel from 'common/components/styleguide/form/_helpers/FormLabel';

const StyledWrapper = styled.div`
  display: inline-block;
  margin-right: 10px;
  &:last-child {
    margin-right: 0;
  }
`;

const StyledRadio = styled.input`
  opacity: 0;
  position: absolute;
  & ~ div {
    &:after {
      display: ${({ checked }) => (checked ? 'block' : 'none')};
  }
`;

const StyledLabel = styled(FormLabel)`
  position: relative;
  display: flex;
  align-items: center;
  cursor: pointer;
  transition: ${({ theme }) => _.get(theme, 'radio.transition')};
  &:hover > div {
    ${({ theme }) => _.get(theme, 'radio.state.hover', null)}
  }
  &:active > div {
    ${({ theme }) => _.get(theme, 'radio.state.active', null)}
  }
  &:focus > div {
    ${({ theme }) => _.get(theme, 'radio.state.focus', null)}
  }
`;

const StyledIndicator = styled.div`
  position: relative;
  display: block;
  width: 20px;
  min-width: 20px;
  height: 20px;
  min-height: 20px;
  border: 1px solid;
  background: #fff;
  border-radius: 50%;
  border-color: ${({ theme, error, isDisabled }) => {
    let color = _.get(theme, 'input.borderColor', _.get(theme, 'border.default.color'));
    if (error) {
      color = _.get(theme, 'colors.danger');
    }
    if (isDisabled) {
      color = _.get(theme, 'colors.disabled');
    }
    return color;
  }};
  margin-right: ${({ labelRight }) => (labelRight ? '5px' : 0)};
  margin-left: ${({ labelRight }) => (labelRight ? 0 : '5px')};
  transition: ${({ theme }) => _.get(theme, 'radio.transition')};
  &:hover {
    ${({ theme }) => _.get(theme, 'radio.state.hover', null)}
  }
  &:active {
    ${({ theme }) => _.get(theme, 'radio.state.active', null)}
  }
  &:focus {
    ${({ theme }) => _.get(theme, 'radio.state.focus', null)}
  }
  &:after {
    display: none;
    content: '';
    position: absolute;
    left: 50%;
    top: 50%;
    border-radius: 50%;
    width: 8px;
    height: 8px;
    margin-top: -4px;
    margin-left: -4px;
    background-color: ${({ theme, error, isDisabled }) => {
      let color = _.get(
        theme,
        'radio.indicatorColor',
        _.get(theme, 'colors.primary')
      );
      if (error) {
        color = _.get(theme, 'colors.danger');
      }
      if (isDisabled) {
        color = _.get(theme, 'colors.disabled');
      }
      return color;
    }};
  }
`;

class Radio extends Component {
  state = {
    isChecked: {}
  };
  static getDerivedStateFromProps(props, state) {
    const { setValueChecked } = props;
    if (setValueChecked && setValueChecked.value) {
      const { value, isChecked = false } = setValueChecked;
      return { ...state, ...{ isChecked: { [value]: isChecked } } };
    }
    return state;
  }
  handleChange = e => {
    const { onChange } = this.props;
    const value = e.target.value;
    const isChecked = e.target.checked;
    if (isChecked) {
      this.setState({
        isChecked: {
          [value]: true
        }
      });
    } else {
      this.setState({ isChecked: {} });
    }
    if (onChange) {
      onChange(e);
    }
  };
  render() {
    const {
      className,
      error,
      name,
      disabled = false,
      showIndicator = true,
      options = []
    } = this.props;
    const { isChecked } = this.state;
    const classNames = [];
    if (className) {
      classNames.push(className);
    }
    if (!_.isEmpty(options)) {
      return (
        <section>
          {_.map(options, option => {
            const optionValue = _.get(option, 'value');
            const optionIsChecked = isChecked[optionValue] ? true : false;
            const cleanedValue = normalizeString(optionValue, '-');
            const hint = _.get(option, 'hint', '');
            let key = sprintf('%1$s-%2$s', name, cleanedValue);
            if (optionIsChecked) {
              key += '-checked';
            }
            const id = sprintf('%1$s-%2$s', name, cleanedValue);
            return (
              <StyledWrapper className={classNames.join(' ')} key={key}>
                <StyledLabel htmlFor={id} error={error} isDisabled={disabled}>
                  <StyledRadio
                    type="radio"
                    id={id}
                    disabled={option.disabled}
                    checked={optionIsChecked}
                    name={name}
                    value={option.value}
                    onChange={this.handleChange}
                  />
                  {showIndicator ? (
                    <StyledIndicator
                      labelRight
                      error={error}
                      isDisabled={disabled}
                    />
                  ) : (
                    ''
                  )}
                  {!_.isEmpty(option.label) ? option.label : null}
                </StyledLabel>
                {hint ? <FormHint>{hint}</FormHint> : null}
              </StyledWrapper>
            );
          })}
          {error ? <FormError>{error}</FormError> : null}
        </section>
      );
    }
    return null;
  }
}

export default Radio;
